import {
  Box,
  Chip,
  FormControl,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Loader } from '@/shared/ui/Loader';
import { NotFoundData } from '@/shared/ui/NotFoundData';
import { Notification } from '@/shared/ui';

import { useCompanyCrmUsers, useUpdateCompanyCrmUserMapping } from '../model/useCompanyCrmUsers';
import { useEmployees } from '../model/useEmployees';

function employeeId(employee: { user_id?: string; userId?: string }) {
  return employee.user_id ?? employee.userId ?? '';
}

function isEmployeeActive(employee: { is_active?: boolean; isActive?: boolean }) {
  return employee.is_active ?? employee.isActive ?? true;
}

function employeeLabel(employee: {
  email?: string | null;
  first_name?: string | null;
  firstName?: string | null;
  last_name?: string | null;
  lastName?: string | null;
  user_id?: string;
  userId?: string;
}) {
  const name = [employee.first_name ?? employee.firstName, employee.last_name ?? employee.lastName]
    .filter(Boolean)
    .join(' ')
    .trim();

  return name || employee.email || employeeId(employee);
}

export const CompanyCrmUserMappingPanel = ({
  companyId,
  ownerId,
}: {
  companyId: string;
  ownerId?: string;
}) => {
  const { t } = useTranslation();
  const crmUsersQuery = useCompanyCrmUsers(companyId);
  const employeesQuery = useEmployees(companyId);
  const updateMapping = useUpdateCompanyCrmUserMapping(companyId);

  const employeeOptions = useMemo(
    () =>
      (employeesQuery.data ?? [])
        .filter((employee) => employeeId(employee) && employeeId(employee) !== ownerId && isEmployeeActive(employee))
        .map((employee) => ({
          id: employeeId(employee),
          label: employeeLabel(employee),
        })),
    [employeesQuery.data, ownerId]
  );

  const knownGochatUserIds = useMemo(
    () => new Set([ownerId, ...employeeOptions.map((employee) => employee.id)].filter(Boolean)),
    [employeeOptions, ownerId]
  );

  const isPending = crmUsersQuery.isPending || employeesQuery.isPending;
  const isError = crmUsersQuery.isError || employeesQuery.isError;
  const error = crmUsersQuery.error ?? employeesQuery.error;
  const crmUsers = crmUsersQuery.data?.users ?? [];

  if (isPending) return <Loader containerSize="medium" />;

  if (isError) {
    return (
      <>
        <NotFoundData title={t('company:crmUserMapping.loadError')} />
        <Notification open={isError} title={error?.message} />
      </>
    );
  }

  if (!crmUsers.length) {
    return <NotFoundData title={t('company:crmUserMapping.empty')} />;
  }

  return (
    <Box>
      <Stack spacing={0.5} sx={{ mb: 2 }}>
        <Typography variant="h6" fontWeight={600}>
          {t('company:crmUserMapping.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('company:crmUserMapping.caption')}
        </Typography>
      </Stack>

      <TableContainer
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          overflowX: 'auto',
        }}
      >
        <Table size="small" sx={{ minWidth: 760 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ minWidth: 260 }}>
                {t('company:crmUserMapping.columns.crmUser')}
              </TableCell>
              <TableCell sx={{ minWidth: 320 }}>
                {t('company:crmUserMapping.columns.gochatUser')}
              </TableCell>
              <TableCell sx={{ width: 140 }}>
                {t('company:crmUserMapping.columns.status')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {crmUsers.map((crmUser) => {
              const currentValue = crmUser.gochat_user_id ?? '';
              const hasUnknownMapping = currentValue && !knownGochatUserIds.has(currentValue);

              return (
                <TableRow key={crmUser.crm_user_id} hover>
                  <TableCell>
                    <Stack spacing={0.25}>
                      <Typography fontWeight={500}>
                        {crmUser.crm_user_name || crmUser.crm_user_id}
                      </Typography>
                      {crmUser.crm_user_email ? (
                        <Typography variant="body2" color="text.secondary">
                          {crmUser.crm_user_email}
                        </Typography>
                      ) : null}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <FormControl size="small" fullWidth>
                      <Select
                        value={currentValue}
                        displayEmpty
                        disabled={updateMapping.isPending}
                        onChange={(event) => {
                          const value = String(event.target.value);
                          updateMapping.mutate({
                            crmUserId: crmUser.crm_user_id,
                            data: { gochat_user_id: value || null },
                          });
                        }}
                      >
                        <MenuItem value="">
                          {t('company:crmUserMapping.unmapped')}
                        </MenuItem>
                        {ownerId ? (
                          <MenuItem value={ownerId}>
                            {t('company:crmUserMapping.owner')}
                          </MenuItem>
                        ) : null}
                        {employeeOptions.map((employee) => (
                          <MenuItem key={employee.id} value={employee.id}>
                            {employee.label}
                          </MenuItem>
                        ))}
                        {hasUnknownMapping ? (
                          <MenuItem value={currentValue} disabled>
                            {t('company:crmUserMapping.unknownUser', { id: currentValue })}
                          </MenuItem>
                        ) : null}
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      color={crmUser.is_active ? 'success' : 'default'}
                      label={crmUser.is_active
                        ? t('company:team.active')
                        : t('company:team.inactive')}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Notification
        open={updateMapping.isError}
        title={updateMapping.error?.message}
      />
    </Box>
  );
};
